import { Collapse, CollapseProps, Stack } from "@mui/material";
import Box, { BoxProps } from "@mui/material/Box";
import Button, { ButtonProps } from "@mui/material/Button";
import ExpandMoreButton from "components/buttons/ExpandMoreButton";
import { ComponentType, Dispatch, SetStateAction, useEffect, useState } from "react";
import { palette, typography } from "theme";

export interface AdditionalFiltersProps {
  onlyIconButtonMode?: boolean;
  collapseProps?: CollapseProps;
  expandedText?: string;
  hiddenText?: string;
  collapseButtonBoxProps?: BoxProps;
  collapseButtonProps?: ButtonProps;
  triggerIsExpanded?: (isExpanded: boolean, setIsExpanded: Dispatch<SetStateAction<boolean>>) => void;
  triggerIsExpandedSignal?: unknown;
  onExpandedChange?: (isExpanded: boolean) => void;
  onExpandedAnimationEnd?: (isExpanded: boolean) => void;
}

const withCollapse = <P extends object>(Component: ComponentType<P>) => {
  const WrappedComponent = ({
    onlyIconButtonMode = false,
    collapseProps,
    expandedText = "скрыть",
    hiddenText = "показать",
    collapseButtonBoxProps,
    collapseButtonProps,
    triggerIsExpanded,
    triggerIsExpandedSignal,
    onExpandedChange,
    onExpandedAnimationEnd,
    ...props
  }: P & AdditionalFiltersProps) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const toggleText = isExpanded ? expandedText : hiddenText;
    const { sx: collapseButtonBoxSx, ...restCollapseButtonBoxProps } = collapseButtonBoxProps || {};
    const { sx: collapseButtonSx, ...restCollapseButtonProps } = collapseButtonProps || {};
    const { onEntered: onEnteredFromProps, onExited: onExitedFromProps, ...restCollapseProps } = collapseProps || {};

    useEffect(() => {
      if (typeof triggerIsExpanded === "function") {
        triggerIsExpanded(isExpanded, setIsExpanded);
      }
    }, [triggerIsExpanded, triggerIsExpandedSignal]);

    useEffect(() => {
      onExpandedChange?.(isExpanded);
    }, [isExpanded, onExpandedChange]);

    const handleEntered: CollapseProps["onEntered"] = (...args) => {
      onEnteredFromProps?.(...args);
      onExpandedAnimationEnd?.(true);
    };

    const handleExited: CollapseProps["onExited"] = (...args) => {
      onExitedFromProps?.(...args);
      onExpandedAnimationEnd?.(false);
    };

    return (
      <Stack sx={{ ...(onlyIconButtonMode && { position: "relative" }) }}>
        <Box
          sx={{
            ...(onlyIconButtonMode && {
              position: "absolute",
              right: 0,
              transform: "translateX(100%)",
            }),
            ...collapseButtonBoxSx,
          }}
          {...restCollapseButtonBoxProps}>
          <Button
            size='small'
            color='secondary'
            disableRipple
            sx={{
              ...typography.body14rg,
              ml: "auto",
              display: "flex",
              mb: 1,
              color: palette.base[200],
              ...(onlyIconButtonMode && { minWidth: "auto" }),
              ...collapseButtonSx,
            }}
            onClick={() => setIsExpanded(!isExpanded)}
            endIcon={
              <ExpandMoreButton
                expand={isExpanded}
                onClick={() => setIsExpanded(!isExpanded)}
              />
            }
            {...restCollapseButtonProps}>
            {!onlyIconButtonMode && toggleText}
          </Button>
        </Box>

        <Collapse
          in={isExpanded}
          timeout='auto'
          unmountOnExit
          onEntered={handleEntered}
          onExited={handleExited}
          {...restCollapseProps}>
          <Component {...(props as P)} />
        </Collapse>
      </Stack>
    );
  };

  return WrappedComponent;
};

export default withCollapse;
